import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slide, { type SlideProps } from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useCallback, useId, useState } from 'react';

// dir="auto": the browser takes the direction from the text itself, so a channel name in Arabic
// or Hebrew reads right to left inside a left-to-right screen (C3 R7). A constant, not a text.
const OWN_DIRECTION = 'auto';

// Bottom sheet of mockup CompteDeconnexion: full width, rounded top corners, above the home bar.
const SHEET_PAPER_SX = {
  m: 0,
  width: '100%',
  maxWidth: 480,
  alignSelf: 'flex-end',
  borderRadius: '22px 22px 0 0',
  px: 2.5,
  pt: 1.25,
  pb: 'calc(28px + env(safe-area-inset-bottom))',
  gap: 2,
} as const;

/** The sheet rises from the bottom of the screen (mockup CompteDeconnexion). */
function SlideUp(props: SlideProps): ReactElement {
  return <Slide {...props} direction="up" />;
}

/** An optional checkbox in the sheet, such as "also close my open positions" (E10). */
interface ConfirmSheetOption {
  /** The checkbox's label, already translated. */
  readonly label: string;
  /** A second line under the label (E10: "closed at market price, in profit or at a loss"). */
  readonly description?: string | undefined;
  /**
   * The option cannot be chosen now (E10: the positions could not be loaded, M4 R6). A disabled
   * option always confirms as unchecked, even if it was checked before it became disabled, and it
   * comes back unchecked when it is enabled again (I7).
   */
  readonly disabled?: boolean | undefined;
  /** Shown under the checkbox, in the same box (mockup 8: the positions the option would close). */
  readonly details?: ReactNode;
}

/** Props of {@link ConfirmSheet}. Every text is a prop, already translated (I12). */
export interface ConfirmSheetProps {
  /** Shown when true; each opening resets the option to unchecked (I7). */
  readonly open: boolean;
  /** The question being confirmed; it names the dialog. */
  readonly title: string;
  /** What will happen, under the title. */
  readonly children: ReactNode;
  /**
   * The confirm button's text. A function receives whether the option is checked, so the label
   * can say what will happen (E10: "Pause and close 2 positions", M4 R4).
   */
  readonly confirmLabel: string | ((optionChecked: boolean) => string);
  /** The cancel button's text; cancel takes the first focus (C3 R9). */
  readonly cancelLabel: string;
  /** `danger` (default) for destructive actions: red confirm button (mockup CompteDeconnexion). */
  readonly tone?: 'danger' | 'primary' | undefined;
  /** Decorative icon shown in a tinted square next to the title. */
  readonly icon?: ReactNode;
  /** Optional checkbox, unchecked on every opening (I7); there is no prop to pre-check it. */
  readonly option?: ConfirmSheetOption | undefined;
  /**
   * Confirm is enabled only while the option is checked, so the sheet cannot confirm a do-nothing
   * choice (E10 "Retry closing", M4 §4 "Close failed"). Without an available option, confirm stays
   * disabled. Default false: confirm works checked or not.
   */
  readonly requireOption?: boolean | undefined;
  /** Shown after the option, before the buttons (E10: the manual-positions note, a failure). */
  readonly footer?: ReactNode;
  /** While the action runs: confirm shows progress, and the sheet cannot be confirmed again or dismissed. */
  readonly busy?: boolean | undefined;
  /** Confirm tapped: `optionChecked` is false when the option is absent or disabled. */
  readonly onConfirm: (result: { readonly optionChecked: boolean }) => void;
  /** Cancel, Escape or the backdrop, unless `busy`. */
  readonly onCancel: () => void;
}

/**
 * Bottom sheet that confirms a destructive action (emergency stop E10, logout E11), the only
 * way to do so across features (C3 R8). A modal dialog: focus starts on the cancel button (the
 * least destructive one), stays inside, Escape and the backdrop cancel, and focus returns to
 * the opener on close (C3 R9; MUI Dialog provides the trap and the return).
 *
 * @example
 * <ConfirmSheet open={open} title={t('…')} confirmLabel={t('…')} cancelLabel={t('…')}
 *   onConfirm={({ optionChecked }) => stop(optionChecked)} onCancel={close}>{t('…')}</ConfirmSheet>
 */
export function ConfirmSheet({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  icon,
  option,
  requireOption = false,
  footer,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmSheetProps): ReactElement {
  const titleId = useId();
  const [optionChecked, setOptionChecked] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  // I7: the option starts unchecked on every opening, even after a check and a cancel.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOptionChecked(false);
  }
  // I7: an option that becomes disabled or disappears forgets its check, so enabling it again
  // shows it unchecked and confirms false until the trader checks it again (M4 R6).
  const available = option !== undefined && option.disabled !== true;
  const [wasAvailable, setWasAvailable] = useState(available);
  if (available !== wasAvailable) {
    setWasAvailable(available);
    if (!available) setOptionChecked(false);
  }
  // Focus the cancel button on open; MUI's focus trap keeps focus that is already inside.
  const focusOnMount = useCallback((button: HTMLButtonElement | null) => {
    button?.focus();
  }, []);

  // A disabled or absent option never confirms as checked (I7: nothing closed by accident).
  const chosen = available && optionChecked;
  const label = typeof confirmLabel === 'string' ? confirmLabel : confirmLabel(chosen);
  const confirmBlocked = requireOption && !chosen;

  const cancel = (): void => {
    if (!busy) onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={cancel}
      fullWidth
      aria-labelledby={titleId}
      slots={{ transition: SlideUp }}
      slotProps={{ paper: { sx: SHEET_PAPER_SX } }}
    >
      <Box
        aria-hidden
        sx={{ alignSelf: 'center', width: 40, height: 5, borderRadius: 3, bgcolor: 'divider' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon !== undefined && (
          <Box
            aria-hidden
            sx={(theme) => ({
              flex: 'none',
              width: 44,
              height: 44,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tone === 'danger' ? theme.fx.danger : theme.fx.brand,
              bgcolor: tone === 'danger' ? theme.fx.dangerBg : theme.fx.infoBg,
            })}
          >
            {icon}
          </Box>
        )}
        {/* R7: a confirmation title is never cut (the trader must read what they confirm); a long
            word such as a channel name breaks instead of widening the sheet at 320 px. dir="auto":
            a right-to-left name keeps its own direction. */}
        <Typography
          id={titleId}
          component="h2"
          dir={OWN_DIRECTION}
          sx={{ fontSize: 22, fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ lineHeight: 1.5 }}>{children}</Box>
      {option !== undefined && (
        // Mockup 8: the option and what it acts on share one bordered box.
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.fx.border}`,
            borderRadius: 3.5,
            overflow: 'hidden',
          })}
        >
          <FormControlLabel
            sx={{
              alignItems: 'flex-start',
              mx: 0,
              p: 1.75,
              width: '100%',
              boxSizing: 'border-box',
            }}
            disabled={busy || option.disabled === true}
            control={
              <Checkbox
                checked={chosen}
                onChange={(event) => {
                  setOptionChecked(event.target.checked);
                }}
                sx={{ mt: -0.75, ml: -0.75 }}
              />
            }
            label={
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {option.label}
                </Box>
                {option.description !== undefined && (
                  <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {option.description}
                  </Box>
                )}
              </Box>
            }
          />
          {option.details !== undefined && (
            <Box
              sx={(theme) => ({
                bgcolor: theme.fx.page,
                borderTop: `1px solid ${theme.fx.neutralBg}`,
                px: 1.75,
                py: 0.75,
              })}
            >
              {option.details}
            </Box>
          )}
        </Box>
      )}
      {footer}
      <Button
        variant="contained"
        size="large"
        color={tone === 'danger' ? 'error' : 'primary'}
        loading={busy}
        aria-busy={busy}
        disabled={confirmBlocked}
        onClick={() => {
          if (!busy && !confirmBlocked) onConfirm({ optionChecked: chosen });
        }}
      >
        {label}
      </Button>
      <Button ref={focusOnMount} variant="text" color="inherit" disabled={busy} onClick={cancel}>
        {cancelLabel}
      </Button>
    </Dialog>
  );
}
